import PropTypes from 'prop-types';
import React from 'react';
import {ButtonIcon} from '@homeaway/react-buttons';
import {CHEVRON_LEFT__16, CHEVRON_RIGHT__16} from '@homeaway/svg-defs';
import classNames from 'classnames';
import './PagerBase.less';

/* istanbul ignore next */
const noop = () => { };

function PagerBase({
    alignCenter,
    alignLeft,
    alignRight,
    children,
    hasNext,
    hasPrev,
    navAriaLabel,
    nextAriaLabel,
    nextUrl,
    onNextClick,
    onPrevClick,
    prevAriaLabel,
    prevUrl,
    wdioPrefix,
    ...rest
}) {
    function handleNextClick(e) {
        e.preventDefault();
        if (hasNext) {
            onNextClick();
        }
    }

    function handlePrevClick(e) {
        e.preventDefault();
        if (hasPrev) {
            onPrevClick();
        }
    }

    const pagerClassName = classNames('Pager', {
        'Pager--right': alignRight
    }, {
        'Pager--left': alignLeft
    }, {
        'Pager--center': alignCenter
    });

    const wdioPrevious = wdioPrefix ? `${wdioPrefix}-pager-previous` : 'pager-previous';
    const wdioNext = wdioPrefix ? `${wdioPrefix}-pager-next` : 'pager-next';

    return (
        <nav aria-label={navAriaLabel} {...rest}>
            <ul className={pagerClassName}>
                <li className="Pager__li Pager__li--previous">
                    <ButtonIcon
                        label={prevAriaLabel}
                        data-wdio={wdioPrevious}
                        circle
                        disabled={!hasPrev}
                        href={prevUrl}
                        svgMarkup={CHEVRON_LEFT__16}
                        aria-label={prevAriaLabel}
                        onClick={handlePrevClick}
                    />
                </li>
                {children}
                <li className="Pager__li Pager__li--next">
                    <ButtonIcon
                        label={nextAriaLabel}
                        data-wdio={wdioNext}
                        circle
                        disabled={!hasNext}
                        href={nextUrl}
                        svgMarkup={CHEVRON_RIGHT__16}
                        aria-label={nextAriaLabel}
                        onClick={handleNextClick}
                    />
                </li>
            </ul>
        </nav>
    );
}

PagerBase.propTypes = {
    /** Flag to align the pager to the right */
    alignRight: PropTypes.bool,
    /** Flag to align the pager to the left */
    alignLeft: PropTypes.bool,
    /** Flag to align the pager in the center */
    alignCenter: PropTypes.bool,
    /** Flag to determine if the pager has more results that can be reached clicking the Next button */
    hasNext: PropTypes.bool.isRequired,
    /** Flag to determine if the pager has more results that can be reached clicking the Prev button */
    hasPrev: PropTypes.bool.isRequired,
    /** The aria-label for the Pager container Nav element, required for assistive technology */
    navAriaLabel: PropTypes.string.isRequired,
    /** The aria-label for the Pager Next Button element, required for assistive technology */
    nextAriaLabel: PropTypes.string.isRequired,
    /** Optional href attribute for the Next button. Used for clients with disabled JavaScript */
    nextUrl: PropTypes.string,
    /** Event handler for when a user clicks the Next button */
    onNextClick: PropTypes.func.isRequired,
    /** Event handler for when a user clicks the Prev button */
    onPrevClick: PropTypes.func.isRequired,
    /** The aria-label for the Pager Prev Button element, required for assistive technology */
    prevAriaLabel: PropTypes.string.isRequired,
    /** Optional href attribute for the Prev button. Used for clients with disabled JavaScript */
    prevUrl: PropTypes.string,
    /** By default, the pager buttons are given data-wdio attributes of "pager-previous" and "pager-next".
    If you have more than one on a page you can add a prefix to those two strings by using this property. */
    wdioPrefix: PropTypes.string
};

PagerBase.defaultProps = {
    hasNext: false,
    hasPrev: false,
    navAriaLabel: 'Pagination',
    nextAriaLabel: 'Next page',
    prevAriaLabel: 'Previous page',
    onNextClick: noop,
    onPrevClick: noop
};

export default PagerBase;
